import clsx from 'clsx';
import _ from 'lodash';
import { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { SelectItem } from '@/core/SelectItem.organism';
import { SelectItemSearchModal } from '@/core/SelectItemSearchModal.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { defaultItemValidation } from '@/formbuilder/SelectItemSearchFormComponent';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { faChartArea, faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Button, Icon } from '@seeqdev/qomponents';
import type { ItemSelectBase } from '@/formbuilder/ItemSelectFormComponent.atom';
import type { SelectedSearchItem } from '@/search/search.types';

export interface ItemSelectAndSearchIF extends Omit<ItemSelectBase, 'label'> {
  component: 'ItemSelectAndSearchFormComponent';
  title: string;
  iconPartialTooltipKey: string;
  searchTypes?: string[];
  scopeIds?: string[];
  excludeGloballyScoped?: boolean;
  helpTextKey?: string;
  showRecentlyAccessed?: boolean;
  validateItem?: (item: SelectedSearchItem) => Promise<boolean>;
  label?: string | React.ReactElement;
}

export const ItemSelectAndSearchFormComponent = (props: ItemSelectAndSearchIF) => {
  const {
    name,
    validation,
    label,
    testId = '',
    value,
    textPlaceholder,
    selectPlaceholder,
    extendValidation,
    extraClassNames,
    customErrorText,
    isMultipleSelect,
    onDetailsButtonClick,
    required = true,
    addAllEnabled = false,
    removeAllEnabled = false,
    closeMenuOnSelect = true,
    onRemoveAll,
    onAddAllClick,
    showOnlyCalculatedItems,
    tooltip,
    tooltipPlacement,
    title,
    iconPartialTooltipKey,
    searchTypes,
    scopeIds,
    helpTextKey,
    showRecentlyAccessed = true,
    validateItem = defaultItemValidation,
  } = props;
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [modalValue, setModalValue] = useState<undefined | SelectedSearchItem>(undefined);

  const defaultValidation = (value: any) => required && _.isEmpty(value) && value?.id !== '';
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const form = useForm();
  const formState = form.getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    (formState.modified?.[name] || formState.submitFailed);

  const resetField = () => {
    form.change(name, undefined);
    form.resetFieldState(name);
  };

  const getInputValueId = (inputValue: any) => (_.isObject(inputValue) ? inputValue.id : inputValue);
  const getSelectedItem = (value: any, inputValue: any) => {
    // in some cases the value is a number, in some cases it's an id and sometimes it's an object - so we do this
    return value !== '' && inputValue !== '' && inputValue !== undefined ? getInputValueId(inputValue) : value;
  };

  const showLabel = label || (onDetailsButtonClick && isMultipleSelect);

  return (
    <FormFieldWrapper
      id={name}
      testId={testId}
      wrapperClassNames={clsx('flexFill', extraClassNames)}
      showError={showError}
      customErrorText={customErrorText}
      label={
        showLabel &&
        label && (
          <div
            className="flexColumnContainer flexAlignBottom flexFill tw:justify-between tw:pb-0.5"
            data-testid={`${testId}Label`}>
            <div className="flexColumnContainer tw:mb-sq-5 tw:items-center">
              <span className={clsx('tw:mr-sq-5')}>{typeof label === 'string' ? t(label) : label}</span>
              {tooltip && (
                <Icon icon={faQuestionCircle} large={true} tooltip={t(tooltip)} tooltipPlacement={tooltipPlacement} />
              )}
            </div>
            {onDetailsButtonClick && isMultipleSelect && (
              <Button
                icon={faChartArea}
                size="xs"
                label={t('DETAILS')}
                tooltip={t('ADD_FROM_DETAILS_PANE')}
                onClick={onDetailsButtonClick}
              />
            )}
          </div>
        )
      }>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const selectedValue =
            isMultipleSelect && Array.isArray(input.value)
              ? _.map(value, (item: any, index: number) => getSelectedItem(item, (input.value as any[])[index]))
              : getSelectedItem(value, input.value);
          const formFieldProps = getFormFieldProps(formState, input, meta, props);
          const selectedModalValue = modalValue && 'id' in modalValue ? [modalValue] : undefined;

          const onSubmitModal = () => {
            if (modalValue && props.onChange) {
              props.onChange(modalValue);
            }
            input.onChange(modalValue);
            setShowModal(false);
          };

          const footer = (
            <div className="flexColumnContainer tw:justify-end tw:p-sq-10">
              <Button
                onClick={() => setShowModal(false)}
                label={t('CANCEL')}
                extraClassNames="mr20"
                testId="cancelButton"
              />
              <Button
                onClick={onSubmitModal}
                label={t('SAVE')}
                variant="theme"
                testId="submitButton"
                disabled={!modalValue}
              />
            </div>
          );

          return (
            <div>
              <SelectItem
                {...formFieldProps}
                onSelect={(item: string | { id: string }, level?: number | string) => {
                  if (props.onChange) {
                    props.onChange(item, level);
                  }
                  input.onChange(item);
                  setModalValue(undefined);
                }}
                showError={formFieldProps.showError}
                selectedItemId={selectedValue}
                selectPlaceholder={selectPlaceholder}
                textPlaceholder={textPlaceholder}
                onToggle={() => resetField()}
                isMultipleSelect={isMultipleSelect}
                addAllEnabled={addAllEnabled}
                removeAllEnabled={removeAllEnabled}
                testId={testId}
                closeMenuOnSelect={isMultipleSelect ? false : closeMenuOnSelect}
                onRemoveAll={onRemoveAll}
                onAddAllClick={onAddAllClick}
                showOnlyCalculatedItems={showOnlyCalculatedItems}
                showSearchButton={true}
                controlClassNames="btrr0 bbrr0"
                onSearchButtonClick={() => setShowModal(true)}
                refetchSelectedItem={modalValue !== undefined && modalValue.id !== selectedValue?.id}
              />
              {showModal ? (
                <SelectItemSearchModal
                  modalId={`${testId}Modal`}
                  onClose={() => {
                    setShowModal(false);
                    setModalValue(undefined);
                  }}
                  iconPartialTooltipKey={iconPartialTooltipKey}
                  onSelect={(item: SelectedSearchItem) => setModalValue(item)}
                  selectItems={
                    typeof selectedValue === 'object' && selectedValue !== null && 'id' in selectedValue
                      ? selectedValue
                      : selectedModalValue
                  }
                  validateItem={validateItem}
                  scopeIds={scopeIds}
                  excludeGloballyScoped={false}
                  body={
                    helpTextKey ? (
                      <Alert dismissible={false} variant="theme" data-testid={`${testId}-helpText`}>
                        {t(helpTextKey)}
                      </Alert>
                    ) : (
                      <></>
                    )
                  }
                  header={{ title: t(title) }}
                  footer={footer}
                  testId={`${testId}Modal`}
                  searchTypes={searchTypes ? searchTypes : []}
                  showRecentlyAccessed={showRecentlyAccessed}
                  onSubmit={onSubmitModal}
                />
              ) : null}
            </div>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
